import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, XCircle, Send, Users, Clock, ExternalLink, RefreshCw, Phone, Building, CreditCard, Globe } from 'lucide-react';

const GrowRegistrationPanel = ({ userId, adminAPI }) => {
  const [formData, setFormData] = useState({
    businessNumber: '',
    phone: '',
    priceQuote: 'eA9tZw',
    isDirectDebit: false,
    website: '',
    sendSms: true
  });
  
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('new');

  useEffect(() => {
    fetchRegistrations();
  }, [userId]);

  const fetchRegistrations = async () => {
    try {
      setLoadingRegistrations(true);
      const response = await adminAPI.growRegistration({
        operation: 'getRegistrations',
        userId
      });
      
      if (response.success) {
        setRegistrations(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching registrations:', err);
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.businessNumber || !formData.phone || !formData.priceQuote) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await adminAPI.growRegistration({
        operation: 'initiateRegistration',
        userId,
        ...formData
      });

      if (response.success) {
        setSuccess({
          message: 'Registration initiated successfully!',
          url: response.registrationUrl,
          registrationId: response.registrationId
        });
        
        setFormData({
          businessNumber: '',
          phone: '',
          priceQuote: '1997',
          isDirectDebit: false,
          website: '',
          sendSms: true
        });
        
        fetchRegistrations();
        setTimeout(() => setActiveTab('history'), 2000);
      } else {
        setError(response.error || 'Registration failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to initiate registration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit();
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { icon: Clock, color: 'text-yellow-600 bg-yellow-50 border-yellow-200', label: 'Pending' },
      completed: { icon: CheckCircle, color: 'text-green-600 bg-green-50 border-green-200', label: 'Completed' },
      failed: { icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200', label: 'Failed' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">GROW Payment Registration</h1>
          <p className="text-gray-600">Register new businesses for payment processing</p>
        </div>

        <div className="flex gap-1 p-1 bg-white/50 backdrop-blur-sm rounded-xl mb-6 shadow-sm">
          <button
            onClick={() => setActiveTab('new')}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'new'
                ? 'bg-white text-purple-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            New Registration
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all relative ${
              activeTab === 'history'
                ? 'bg-white text-purple-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Registration History
            {registrations.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                {registrations.length}
              </span>
            )}
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">Registration Failed</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-green-800 font-medium">{success.message}</p>
                {success.url && (
                  <a 
                    href={success.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-green-600 hover:text-green-700 text-sm font-medium"
                  >
                    Open Registration Form
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'new' && (
          <div className="bg-white/70 backdrop-blur-md rounded-2xl shadow-xl p-6 border border-white/50">
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Building className="w-4 h-4 text-gray-400" />
                    Business Number / ID *
                  </label>
                  <input
                    type="text"
                    name="businessNumber"
                    value={formData.businessNumber}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="e.g., 985557776"
                    className="w-full px-4 py-2.5 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    Contact Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="05X-XXXX-XXX"
                    className="w-full px-4 py-2.5 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <CreditCard className="w-4 h-4 text-gray-400" />
                    Price Quote *
                  </label>
                  <input
                    type="text"
                    name="priceQuote"
                    value={formData.priceQuote}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Package code"
                    className="w-full px-4 py-2.5 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Globe className="w-4 h-4 text-gray-400" />
                    Website (Optional)
                  </label>
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="www.example.com"
                    className="w-full px-4 py-2.5 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-6 pt-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isDirectDebit"
                    disabled={true}
                    checked={formData.isDirectDebit}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Enable Direct Debit</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="sendSms"
                    checked={formData.sendSms}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Send SMS Notification</span>
                </label>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium rounded-xl hover:from-purple-700 hover:to-blue-700 focus:ring-4 focus:ring-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Initiate Registration
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="bg-white/70 backdrop-blur-md rounded-2xl shadow-xl border border-white/50 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Registration History</h2>
                <button
                  onClick={fetchRegistrations}
                  disabled={loadingRegistrations}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 text-gray-600 ${loadingRegistrations ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {loadingRegistrations ? (
              <div className="p-12 text-center">
                <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
                <p className="text-gray-500">Loading registrations...</p>
              </div>
            ) : registrations.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No registrations yet</p>
                <p className="text-gray-400 text-sm mt-1">New registrations will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {registrations.map((reg) => (
                      <tr key={reg.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                          {reg.businessNumber}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {reg.phone}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {reg.packageCode}
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(reg.status)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {reg.createdAt ? new Date(reg.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          {reg.registrationUrl && reg.status === 'pending' && (
                            <a
                              href={reg.registrationUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-700 text-sm font-medium"
                            >
                              Open
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                          {reg.status === 'completed' && reg.growUserId && (
                            <span className="text-xs text-green-600 font-medium">
                              ID: {reg.growUserId.substring(0, 8)}...
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GrowRegistrationPanel;